import { useOidc, useOidcAccessToken, useOidcIdToken, withOidcSecure } from "@axa-fr/react-oidc";
import { selectOidc, setUserData } from "@org-avp/avp-avengers-ui-shared-store";
import { FC, PropsWithChildren, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { IdpConfiguration, OidcAppConfiguration, OidcContext } from "../../types/oidcTypes";
import { persistPreAuthenticationPathIfNoneIsSet } from "../../utils/oidcUtils";
import { OidcWaiting } from "./OidcStateComponents";

export interface OidcGuardProps {
  idpConfiguration: IdpConfiguration;
  oidcAppConfiguration: OidcAppConfiguration;
}

export const OidcGuard: FC<PropsWithChildren<OidcGuardProps>> = ({ children, idpConfiguration, oidcAppConfiguration }) => {
  const OidcSecuredPageContent = withOidcSecure(OidcPageContent, null, null, oidcAppConfiguration.oidcContext);
  return (
    <OidcInit idpConfiguration={idpConfiguration} oidcAppConfiguration={oidcAppConfiguration}>
      <OidcSecuredPageContent>
        {children}
      </OidcSecuredPageContent>
    </OidcInit>
  );
};

const OidcPageContent: FC<PropsWithChildren> = ({ children }) => {
  const oidcState = useSelector(selectOidc);
  return (
    <>
      {oidcState.userData == null
        ? <OidcWaiting/>
        : children}
    </>
  );
};

const OidcInit: FC<PropsWithChildren<OidcGuardProps>> = ({ children, idpConfiguration, oidcAppConfiguration }) => {
  const { accessToken, accessTokenPayload } = useOidcAccessToken(oidcAppConfiguration.oidcContext);
  const { idToken, idTokenPayload } = useOidcIdToken(oidcAppConfiguration.oidcContext);
  const { isAuthenticated } = useOidc(oidcAppConfiguration.oidcContext);

  const dispatch = useDispatch();
  const navigate = useNavigate();

  useEffect(
    () => {
      const isUserDataComplete = isAuthenticated
        && typeof accessToken === "string" && typeof idToken === "string"
        && accessTokenPayload != null && typeof accessTokenPayload === "object"
        && idTokenPayload != null && typeof idTokenPayload === "object";

      if (isUserDataComplete) {
        const userInfo = {
          ...accessTokenPayload,
          ...idTokenPayload,
        };

        // only set user data, if the token is not expired already, and if the issuer matches the configured authority
        if ((userInfo.exp ?? 0) > Date.now() / 1000 && userInfo.iss === idpConfiguration.authority) {
          // only update the user data for our default login (our own IdP)
          if (oidcAppConfiguration.oidcContext === OidcContext.DEFAULT) {
            dispatch(setUserData({ userInfo, accessToken, idToken }));
          }

          return;
        }

        // navigate to logout page, if token is expired
        persistPreAuthenticationPathIfNoneIsSet();
        navigate(oidcAppConfiguration.pathLogout);
      }

      // only remove the user data for our default login (our own IdP)
      if (oidcAppConfiguration.oidcContext === OidcContext.DEFAULT) {
        dispatch(setUserData(null));
      }
    },
    [
      accessToken,
      accessTokenPayload,
      dispatch,
      idToken,
      idTokenPayload,
      idpConfiguration.authority,
      isAuthenticated,
      navigate,
      oidcAppConfiguration.oidcContext,
      oidcAppConfiguration.pathLogout,
    ],
  );

  return (<>{children}</>);
};
